import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, ArrowLeft } from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import api, { apiError } from "@/lib/api";
import { useAuth, homeFor } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PhoneInput from "@/components/PhoneInput";

export default function ClaimAccount() {
  const { applyAuth } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("+255");
  const [otp, setOtp] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);

  const sendOtp = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/request", { phone });
      toast.success("OTP sent to your phone");
      setStep(2);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };

  const claim = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/customer/claim", { phone, otp, pin });
      const user = applyAuth(data);
      toast.success(`Welcome, ${user.name}!`);
      nav(homeFor(user.role));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen ok-bg grain relative flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <Logo className="h-9" />
        </Link>
        <div className="card-surface p-7 fade-up">
          <h1 className="font-display font-bold text-2xl mb-1 flex items-center gap-2"><CreditCard size={24} className="text-primary" /> Claim your account</h1>
          <p className="text-sm text-muted-foreground mb-6">Have a OneKite card from a shop? Verify your number to set a PIN and get full app access — your points and history stay the same.</p>

          {step === 1 && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">Phone number</Label>
                <PhoneInput testid="claim-phone" value={phone} onChange={setPhone} />
              </div>
              <Button data-testid="claim-send-otp" onClick={sendOtp} disabled={loading || !phone} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                {loading ? "Sending…" : "Send code"}
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">Verification code</Label>
                <Input data-testid="claim-otp" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit code" className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Set a PIN (4 or 6 digits)</Label>
                <Input data-testid="claim-pin" type="password" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="••••" className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" />
              </div>
              <Button data-testid="claim-confirm" onClick={claim} disabled={loading || !otp || !pin} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                {loading ? "Claiming…" : "Claim & sign in"}
              </Button>
            </div>
          )}

          <Link to="/login" className="mt-6 flex items-center gap-1 justify-center text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft size={14} /> Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
